import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';

import compare from 'just-compare';
import throttle from 'just-throttle';
import { History } from 'react-router';
import {
  OnboardingData,
  OnboardingOptions,
  OnboardingUpdates,
} from 'registration/types';

import { LucideIcon } from 'shared/components/ihcl/lucideIcon';
import { styled, useStyletron, withStyle } from 'shared/components/ihcl/styled';
import { Button } from 'shared/components/ihcl/v2/button';
import { ProgressSteps } from 'shared/components/ihcl/v2/progress-steps';
import { toast } from 'shared/components/ihcl/v2/toast';
import { pathToVariant } from 'shared/helpers/activateABTest';
import { doPatch } from 'shared/helpers/http';
import {
  trackEvent,
  trackProfileComplete,
  trackViewContent,
} from 'shared/helpers/tracking';

import { TalentProfile } from 'talent/types';

import {
  OnboardingBackArrow,
  OnboardingTopNavSpacer,
} from '../components/non_hiring/pages/styledComponents';
import isReactivationFlow from '../helpers/isReactivationFlow';
import {
  augmentState,
  convertStateToAttributes,
  convertToState,
} from '../helpers/talentProfileConversions';

type OnboardingContextType = {
  onboardingBasePath: string;
  onboardingData: OnboardingData;
  onboardingOptions: OnboardingOptions;
  onboardingUpdates?: OnboardingUpdates;
  setOnboardingData?: React.Dispatch<React.SetStateAction<OnboardingData>>;
  history: History;
  isLiteProfileActivating?: boolean;
  isUpdatingProfile?: boolean;
  profileType?: string;
  setOnboardingUpdates?: (updates: OnboardingUpdates) => void;
  totalSteps?: number;
  skippedSteps?: number[];
  priorStep?: number;
  setIsUpdatingProfile?: React.Dispatch<React.SetStateAction<boolean>>;
};

export const OnboardingContext = React.createContext<OnboardingContextType>({
  onboardingBasePath: '',
  onboardingData: {},
  onboardingOptions: undefined,
  onboardingUpdates: {
    current_reactivation_step: 0,
    current_onboard_step: 0,
    talent_advocate: undefined,
    recent_grad: false,
  },
  setOnboardingData: undefined,
  history: undefined,
  profileType: undefined,
  setOnboardingUpdates: undefined,
  totalSteps: undefined,
  skippedSteps: undefined,
  priorStep: undefined,
});

export const RestrictedWidthContainer = styled('div', {
  marginLeft: 'auto',
  marginRight: 'auto',
  maxWidth: '960px',
  minWidth: '320px',
  width: '100%',
});

export const TagLocationPinIcon = () => (
  <LucideIcon
    name="MapPin"
    size="15px"
    style={{ marginBottom: '-3px', marginRight: '6px' }}
  />
);

export const OnboardingWrapper = withStyle<
  any,
  {
    $flushSides?: boolean;
    $compactMaxWidth?: boolean;
    $justifyContent?: string;
  }
>(
  RestrictedWidthContainer,
  ({
    $theme,
    $flushSides = false,
    $compactMaxWidth = false,
    $justifyContent = 'flex-start',
  }) => ({
    backgroundColor: $theme.colors.onboardingBackground,
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    justifyContent: $justifyContent,
    height: 'calc(var(--vh, 1vh) * 100)',
    maxWidth: $compactMaxWidth ? '560px' : '960px',
    paddingTop: $compactMaxWidth ? $theme.sizing.unit8 : $theme.sizing.unit32,
    paddingRight: $flushSides ? 0 : $theme.sizing.unit16,
    paddingBottom: $theme.sizing.unit32,
    paddingLeft: $flushSides ? 0 : $theme.sizing.unit16,
    '@media screen and (max-width: 340px)': {
      paddingLeft: '0',
      paddingRight: '0',
    },
  })
);

export const ProgressStepsWrapper = withStyle<
  any,
  { $flushWrapperSides?: boolean; $accommodateBackButton?: boolean }
>(
  RestrictedWidthContainer,
  ({ $theme, $flushWrapperSides = false, $accommodateBackButton = false }) => {
    const backButtonWrapperStyles = $accommodateBackButton
      ? {
          display: 'inline-flex',
          alignItems: 'center',
          gap: $theme.sizing.unit16,
        }
      : {};
    return {
      paddingRight: $flushWrapperSides ? $theme.sizing.unit16 : 0,
      paddingLeft: $flushWrapperSides ? $theme.sizing.unit16 : 0,
      boxShadow: `0 ${$theme.sizing.unit10} ${$theme.sizing.unit20} ${$theme.colors.onboardingBackground}`,
      ...backButtonWrapperStyles,
    };
  }
);

export const StepNavigation = withStyle<any, { $flushWrapperSides?: boolean }>(
  RestrictedWidthContainer,
  ({ $theme, $flushWrapperSides = false }) => ({
    position: 'relative',
    display: 'flex',
    paddingRight: $flushWrapperSides ? $theme.sizing.unit16 : 0,
    paddingLeft: $flushWrapperSides ? $theme.sizing.unit16 : 0,
    boxShadow: `0 -10px 20px ${$theme.colors.onboardingBackground}`,
    justifyContent: 'space-between',
  })
);

const StepContentsWrapper = styled('div', {
  height: '100%',
  overflow: 'auto',
  paddingBottom: '20px',
  ':after': {
    content: '',
    display: 'table',
    clear: 'both',
  },
});

const StepContentsInnerWrapper = withStyle<
  any,
  {
    $autoSpacedStepContents: boolean;
    $centerStepContents: boolean;
    $customStepContentsStyle: object;
  }
>(
  RestrictedWidthContainer,
  ({
    $autoSpacedStepContents,
    $centerStepContents,
    $customStepContentsStyle,
  }): any => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: $centerStepContents ? 'center' : 'initial',
    justifyContent: $autoSpacedStepContents ? 'space-around' : 'normal',
    minHeight: '100%',
    ...$customStepContentsStyle,
  })
);

type StepContentsProps = {
  autoSpacedStepContents?: boolean;
  centerStepContents?: boolean;
  children: React.ReactNode;
  customStepContentsStyle?: object;
};

export const StepContents = ({
  autoSpacedStepContents = false,
  centerStepContents = false,
  children,
  customStepContentsStyle = null,
}: StepContentsProps) => (
  <StepContentsWrapper>
    <StepContentsInnerWrapper
      $autoSpacedStepContents={autoSpacedStepContents}
      $centerStepContents={centerStepContents}
      $customStepContentsStyle={customStepContentsStyle}
    >
      {children}
    </StepContentsInnerWrapper>
  </StepContentsWrapper>
);

export const OnboardingTitle = styled<
  'h2',
  { $noBottomMargin?: boolean; $noTopMargin?: boolean }
>('h2', ({ $theme, $noBottomMargin = false, $noTopMargin = false }) => {
  const baseStyles = $theme.typography.HeadingMedium;

  return {
    ...baseStyles,
    textAlign: 'center' as const,
    marginTop: $noTopMargin ? '0' : $theme.sizing.unit40,
    marginBottom: $noBottomMargin ? '0' : $theme.sizing.unit40,
  };
});

export const OnboardingSubtitle = styled<
  'h3',
  {
    $fontWeight?: string;
    $noTopMargin?: boolean;
    $smallTopMargin?: boolean;
    $largeBottomMargin?: boolean;
    $noBottomMargin?: boolean;
  }
>(
  'h3',
  ({
    $theme,
    $fontWeight = null,
    $noTopMargin = false,
    $smallTopMargin = false,
    $largeBottomMargin = false,
    $noBottomMargin = false,
  }) => {
    let marginTop: string = $theme.sizing.unit40;
    if ($noTopMargin) {
      marginTop = '0';
    }
    if ($smallTopMargin) {
      marginTop = $theme.sizing.unit16;
    }
    let marginBottom: string = $theme.sizing.unit16;
    if ($noBottomMargin) {
      marginBottom = '0';
    } else if ($largeBottomMargin) {
      marginBottom = $theme.sizing.unit32;
    } else {
      marginBottom = $theme.sizing.unit16;
    }

    return {
      ...$theme.typography.LabelMedium,
      fontWeight: $fontWeight || 'unset',
      marginBottom,
      marginTop,
    };
  }
);

export const OnboardingText = styled('p', ({ $theme }) => ({
  color: $theme.colors.bodyCopyGray,
  fontSize: '18px',
  marginBottom: '32px',
}));

export const nextStepNumber = (currentStep) =>
  (parseInt(currentStep, 10) || 1) + 1;

export const getNextAction = (basePath, currentStep, totalSteps) =>
  `${basePath}/${Math.min(nextStepNumber(currentStep), totalSteps)}`;

export const getPreviousAction = (basePath, currentStep) =>
  `${basePath}/${Math.max(currentStep - 1, 1)}`;

type SendUpdateProps = {
  currentStep: number;
  history: any;
  nextAction: string;
  onboardingBasePath: string;
  onboardingData: any;
  onboardingUpdates: any;
  previousOnboardingUpdates?: any;
  profileType?: string;
  sharedEventProperties?: any;
  setIsUpdating?: (isUpdating: boolean) => void;
  setOnboardingData?: (data: any) => void;
  setOnboardingUpdates?: (updates: any) => void;
  stepName: string;
  toasterUtil?: any;
  totalSteps?: number;
  formData?: any;
  skipOnboardStepUpdate?: boolean;
};

export const sendUpdate = ({
  currentStep,
  history,
  nextAction,
  onboardingBasePath,
  onboardingData,
  onboardingUpdates,
  previousOnboardingUpdates,
  profileType,
  sharedEventProperties,
  setIsUpdating = () => {},
  setOnboardingData,
  setOnboardingUpdates,
  stepName,
  toasterUtil,
  totalSteps,
  formData,
  skipOnboardStepUpdate = false,
}: SendUpdateProps) => {
  setIsUpdating(true);
  const navigateToNextAction = (nextActionUrl) => {
    if (nextActionUrl.indexOf(onboardingBasePath) === 0) {
      history.push(nextActionUrl);
    } else {
      window.location.href = new URL(
        nextActionUrl,
        window.location.origin
      ).toString();
    }
  };

  const flowType = isReactivationFlow(onboardingBasePath)
    ? 'Reactivation'
    : 'Onboard';
  const onboardingUpdatesWithStep: Record<string, unknown> = Object.fromEntries(
    Object.entries(onboardingUpdates || {}).filter(
      // Removing keys without values allows server-side updates & localforage
      // to rehydrate properly (without conflict) if set later.
      ([, value]) => value !== undefined
    )
  );
  const nextStep = Number(nextAction.replace(`${onboardingBasePath}/`, ''));
  const savedStep = isReactivationFlow(onboardingBasePath)
    ? onboardingData.current_reactivation_step
    : onboardingData.current_onboard_step;

  if (
    !skipOnboardStepUpdate &&
    !Number.isNaN(nextStep) &&
    Number(nextStep) > 0 &&
    (!savedStep || nextStep > savedStep)
  ) {
    if (isReactivationFlow(onboardingBasePath)) {
      onboardingUpdatesWithStep.current_reactivation_step = nextStep;
    } else {
      onboardingUpdatesWithStep.current_onboard_step = nextStep;
    }
  }

  if (onboardingData.id) {
    if (onboardingUpdates && formData) {
      throw new Error(
        'Only supply one of onboardingUpdates or formData or ' +
          'else not all changes will be persisted'
      );
    }
    let updates;
    if (formData) {
      updates = formData;
      updates.append('include_conversion_multiplier', true);
    } else {
      updates = {
        ...onboardingUpdatesWithStep,
        include_conversion_multiplier: true,
      };
    }

    const type = profileType === 'tech' ? 'tech' : 'nurse';

    doPatch(`/${type}_profile/${onboardingData.id}`, updates)
      .then((resp: TalentProfile) => {
        trackEvent(`${flowType} Step ${currentStep} Completed`, {
          ...sharedEventProperties,
          step_name: stepName,
        });
        let newOnboardingData = {
          ...onboardingData,
        };
        let onboardingDataUpdated = false;
        if (currentStep === 2) {
          trackViewContent(resp);
        }
        if (resp.type === 'NurseProfile' && resp.nurse_specialties) {
          // The resume step relies on this data in the converted form.
          newOnboardingData = {
            ...newOnboardingData,
            nurse_specialties: resp.nurse_specialties,
            ...convertToState(resp, ['nurseSpecialties']),
          };
          onboardingDataUpdated = true;
        }
        if (resp.type === 'NurseProfile' && resp.certifications) {
          newOnboardingData = {
            ...newOnboardingData,
            ...augmentState(resp, {}, ['certifications']),
          };
          onboardingDataUpdated = true;
        }
        if (resp.talent_advocate && !onboardingData.talent_advocate) {
          newOnboardingData = {
            ...newOnboardingData,
            talent_advocate: {
              ...resp.talent_advocate,
            },
          };
          onboardingDataUpdated = true;
        }
        if (resp.screen_status !== onboardingData.screen_status) {
          newOnboardingData.screen_status = resp.screen_status;
          onboardingDataUpdated = true;
        }
        if (
          typeof resp.connectable === 'boolean' &&
          resp.connectable !== onboardingData.connectable
        ) {
          newOnboardingData.connectable = resp.connectable;
          onboardingDataUpdated = true;
        }
        if (
          resp.type === 'NurseProfile' &&
          resp.has_job_matches !== onboardingData.has_job_matches
        ) {
          newOnboardingData.has_job_matches = resp.has_job_matches;
          onboardingDataUpdated = true;
        }
        if (
          resp.type === 'NurseProfile' &&
          resp.screening_type !== onboardingData.screening_type
        ) {
          newOnboardingData.screening_type = resp.screening_type;
          onboardingDataUpdated = true;
        }
        if (
          !onboardingData.currentLocation ||
          resp.current_zip_code !== onboardingData.currentLocation.zip_code
        ) {
          const label = `${resp.current_city}, ${resp.current_state}`;
          newOnboardingData.currentLocation = {
            city: resp.current_city,
            state: resp.current_state,
            zip_code: resp.current_zip_code,
            label,
            id: `${label}, United States`,
          };
          onboardingDataUpdated = true;
        }
        if (
          resp.type === 'NurseProfile' &&
          resp.conversion_multiplier !== onboardingData.conversion_multiplier
        ) {
          newOnboardingData.conversion_multiplier = resp.conversion_multiplier;
          onboardingDataUpdated = true;
        }
        if (resp.type === 'TechProfile' && resp.tech_job_descriptions) {
          newOnboardingData = {
            ...newOnboardingData,
            tech_job_descriptions: resp.tech_job_descriptions,
            ...convertToState(resp, ['techRoleOptions']),
          };
          onboardingDataUpdated = true;
        }
        if (resp.type === 'TechProfile' && resp.tech_custom_certifications) {
          newOnboardingData = {
            ...newOnboardingData,
            tech_custom_certifications: resp.tech_custom_certifications,
            ...convertToState(resp, ['techCustomCertifications']),
          };
          onboardingDataUpdated = true;
        }
        if (resp.type === 'TechProfile' && resp.tech_certifications) {
          newOnboardingData = {
            ...newOnboardingData,
            tech_certifications: resp.tech_certifications,
            ...convertToState(resp, ['techCertifications']),
          };
          onboardingDataUpdated = true;
        }
        if (resp.type === 'TechProfile') {
          Object.assign(
            newOnboardingData,
            [
              'day_shift',
              'mid_shift',
              'night_shift',
              'full_time',
              'part_time',
              'per_diem',
            ].reduce((acc, key) => ({ ...acc, [key]: resp[key] }), {}),
            convertToState(resp, ['employment_type']),
            convertToState(resp, ['shift_preferences'])
          );
          onboardingDataUpdated = true;
        }
        if (resp.type === 'TechProfile' && resp.talent_education_experiences) {
          newOnboardingData = {
            ...newOnboardingData,
            talent_education_experiences: resp.talent_education_experiences,
            ...convertToState(resp, ['talentEducationExperiences']),
          };
          onboardingDataUpdated = true;
        }
        if (onboardingDataUpdated && setOnboardingData) {
          setOnboardingData(newOnboardingData);
        }

        if (currentStep === totalSteps) {
          trackProfileComplete(resp).finally(() => {
            navigateToNextAction(nextAction);
          });
        } else {
          navigateToNextAction(nextAction);
        }
      })
      .catch((error) => {
        setIsUpdating(false);
        if (error.response && error.response.status === 422) {
          error.response.json().then((json) => {
            if (json.errors && setOnboardingData) {
              setOnboardingData({
                ...onboardingData,
                errors: json.errors,
              });
            } else {
              console.error(json);
            }
          });
        } else {
          console.error(error);
          const errorMsg =
            'Oops, there was a problem connecting ' +
            'to the server. Please check your internet connection ' +
            'and try again, or refresh the page.';
          if (toasterUtil) {
            toasterUtil.error(errorMsg);
          }
        }
      });
  } else {
    const newOnboardingData = {
      ...onboardingData,
    };
    if (typeof window.localforage !== 'undefined') {
      window.localforage.getItem(
        'onboardingUpdates',
        (err, localOnboardingUpdates: object) => {
          const newOnboardingUpdates = {
            ...localOnboardingUpdates,
            ...previousOnboardingUpdates,
            ...onboardingUpdatesWithStep,
          };
          const changedKeys = Object.keys(onboardingUpdates).filter((key) => {
            // Skip keys that are not user editable
            if (['resume_parse_task_id', 'summary'].includes(key)) {
              return false;
            }
            return !compare(
              onboardingUpdates[key],
              previousOnboardingUpdates[key]
            );
          });
          if (
            previousOnboardingUpdates?.resume_parse_task_id &&
            changedKeys.length > 0
          ) {
            trackEvent('Parsed resume fields updated', {
              changedKeys: changedKeys.join(', '),
              step: previousOnboardingUpdates.current_onboard_step,
            });
          }
          window.localforage.setItem(
            'onboardingUpdates',
            newOnboardingUpdates,
            (setErr) => {
              if (setErr) {
                console.error('Local Onboarding Data Save Error:', setErr);
              }
              if (setOnboardingUpdates) {
                setOnboardingUpdates(newOnboardingUpdates);
              }
              if (setOnboardingData) {
                setOnboardingData(newOnboardingData);
              }
              trackEvent(
                `${flowType} Step ${currentStep} Completed`,
                sharedEventProperties
              );
              navigateToNextAction(nextAction);
            }
          );
        }
      );
    } else {
      if (setOnboardingUpdates) {
        const newOnboardingUpdates = {
          ...previousOnboardingUpdates,
          ...onboardingUpdatesWithStep,
        };
        setOnboardingUpdates(newOnboardingUpdates);
      }
      if (setOnboardingData) {
        setOnboardingData(newOnboardingData);
      }
      trackEvent(`${flowType} Step ${currentStep} Completed`);
      navigateToNextAction(nextAction);
    }
  }
};

const setVhProperty = () => {
  const vh = window.innerHeight * 0.01;
  document.documentElement.style.setProperty('--vh', `${vh}px`);
};

type OnboardingProps = {
  autoProgress?: boolean;
  autoSpacedStepContents?: boolean;
  centerStepContents?: boolean;
  children: React.ReactNode;
  currentStep?: number;
  customStepContentsStyle?: object;
  flushWrapperSides?: boolean;
  isNextDisabled: boolean;
  nextAction?: string;
  nextActionButtonRef?: any; // TODO: Fix this type
  nextButtonSubmits?: boolean;
  onboardingWrapper?: React.ComponentType<
    PropsWithChildren<{
      $flushSides: boolean;
      $compactMaxWidth: boolean;
      $wrapperProps: any; // TODO fix this type
    }>
  >;
  onboardingWrapperProps?: object;
  previousAction?: string;
  skipOnboardStepUpdate?: boolean;
  stepKeys?: string[];
  stepName?: string;
  validateBeforeSubmit?: Function;
};

export const Onboarding = ({
  autoProgress = false,
  autoSpacedStepContents = false,
  centerStepContents = false,
  children,
  currentStep = 1,
  customStepContentsStyle = null,
  flushWrapperSides = false,
  isNextDisabled,
  nextActionButtonRef = null,
  nextButtonSubmits = false,
  onboardingWrapper = null,
  onboardingWrapperProps = {},
  stepKeys = [],
  stepName = '[unknown]',
  validateBeforeSubmit = null,
  ...props
}: OnboardingProps) => {
  const {
    onboardingData,
    onboardingUpdates: previousOnboardingUpdates,
    profileType,
    setOnboardingData,
    setOnboardingUpdates,
  } = useContext(OnboardingContext);
  let { onboardingBasePath } = useContext(OnboardingContext);
  let { nextAction, previousAction } = props;
  if (onboardingBasePath.endsWith('/')) {
    onboardingBasePath = onboardingBasePath.substring(
      0,
      onboardingBasePath.length - 1
    );
  }
  const { totalSteps, skippedSteps, priorStep, history } =
    useContext(OnboardingContext);
  const hiddenSteps = skippedSteps ?? [];
  const visibleTotalSteps = totalSteps - hiddenSteps.length;
  const visibleCurrentStep =
    currentStep - hiddenSteps.filter((step) => step < currentStep).length;
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [, theme] = useStyletron();
  let setIsUpdating = setIsUpdatingProfile;
  if (autoProgress) {
    // setIsUpdating is only needed to disable the next button when the next
    // step is being loaded. Use a no-op when autoProgress is enabled.
    setIsUpdating = () => {};
  }

  const variant = pathToVariant(
    onboardingBasePath,
    profileType === 'tech' ? 'techOnboarding' : 'onboarding'
  );

  const flowType = isReactivationFlow(onboardingBasePath)
    ? 'Reactivation'
    : 'Onboard';

  const sharedEventProperties = {
    onboard_variant: variant,
    profile_type: profileType,
  };

  useEffect(() => {
    if (history.action === 'POP' || history.location?.state?.back) {
      trackEvent(`${flowType} Navigated Back to Step ${currentStep}`, {
        ...sharedEventProperties,
        step_name: stepName,
      });
    }
  }, [currentStep, history]);

  useEffect(() => {
    trackEvent(`${flowType} Step ${currentStep} Started`, {
      ...sharedEventProperties,
      step_name: stepName,
    });

    setVhProperty();
    // This prevents the container from being scrolled around on iOS
    document.documentElement.style.position = 'fixed';
    document.documentElement.style.height = '100%';
    document.documentElement.style.width = '100%';
    window.addEventListener(
      'resize',
      throttle(setVhProperty, 200, { leading: true })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  if (!nextAction) {
    nextAction = getNextAction(onboardingBasePath, currentStep, totalSteps);
  }
  if (!previousAction) {
    previousAction = getPreviousAction(onboardingBasePath, currentStep);
  }
  // In some cases we want to explicitly prevent the back button from appearing
  // so that users can't go back to the Sign Up page. The # represents a null
  // action that we don't want to automatically populate based on currentStep.
  if (previousAction === '#') {
    previousAction = null;
  }

  const submitUpdate = () => {
    if (validateBeforeSubmit) {
      if (!validateBeforeSubmit()) {
        return;
      }
    }
    if (stepKeys.length) {
      const stepObj = stepKeys.reduce((acc, key) => {
        acc[key] = onboardingData[key];
        return acc;
      }, {});
      if (Object.keys(stepObj).includes('skip')) {
        // @ts-ignore
        delete stepObj.skip;
      }
      const onboardingUpdates = convertStateToAttributes(
        stepObj,
        Object.keys(stepObj),
        onboardingData
      );

      sendUpdate({
        currentStep,
        history,
        nextAction,
        onboardingBasePath,
        onboardingData,
        onboardingUpdates,
        previousOnboardingUpdates,
        profileType,
        sharedEventProperties,
        setIsUpdating,
        setOnboardingData,
        setOnboardingUpdates,
        stepName,
        toasterUtil: toast,
        totalSteps,
      });
    }
  };

  useEffect(() => {
    if (autoProgress && !isNextDisabled) {
      submitUpdate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoProgress, isNextDisabled]);

  if (onboardingWrapper) {
    const Wrapper = onboardingWrapper;
    return (
      <Wrapper
        $flushSides={flushWrapperSides}
        $compactMaxWidth
        $wrapperProps={{
          ...onboardingWrapperProps,
          isNextDisabled,
          validateBeforeSubmit,
        }}
      >
        {children}
      </Wrapper>
    );
  }

  return (
    <OnboardingWrapper $flushSides={flushWrapperSides} $compactMaxWidth>
      {totalSteps > 0 && (
        <ProgressStepsWrapper $accommodateBackButton>
          {previousAction && (
            <Button
              type="button"
              variant="minimal"
              color="alternate"
              onClick={() => {
                if (
                  currentStep === 2 &&
                  history.location.search.includes(
                    'job_search_status=unselected'
                  )
                ) {
                  // This requires an extra step back because there is a redirect to add the
                  // variant to the URL. We go from /_/1 -> /z/1
                  history.go(-2);
                } else {
                  history.push(previousAction, { back: true });
                }
              }}
              data-testid="onboarding-compact-back-button"
            >
              <OnboardingBackArrow name="ArrowLeft" />
            </Button>
          )}
          {!previousAction && <OnboardingTopNavSpacer />}
          <ProgressSteps
            currentStep={visibleCurrentStep}
            totalSteps={visibleTotalSteps}
            celebrateLastStep={priorStep && priorStep < currentStep}
          />
          <OnboardingTopNavSpacer />
        </ProgressStepsWrapper>
      )}
      <StepContents
        autoSpacedStepContents={autoSpacedStepContents}
        centerStepContents={centerStepContents}
        customStepContentsStyle={customStepContentsStyle}
      >
        {children}
      </StepContents>
      <StepNavigation $flushWrapperSides={flushWrapperSides}>
        {!previousAction && <span />}
        {!autoProgress && (
          <Button
            fullWidth
            ref={nextActionButtonRef}
            type={nextButtonSubmits ? 'submit' : 'button'}
            color="alternate"
            disabled={isNextDisabled || isUpdatingProfile}
            loading={isUpdatingProfile}
            onClick={() => submitUpdate()}
          >
            Next
            <LucideIcon name="ArrowRight" />
          </Button>
        )}
        {autoProgress && <span />}
      </StepNavigation>
    </OnboardingWrapper>
  );
};
